import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { describedBy, Field } from './Field';
import styles from './form.module.css';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  /** Rendered inside the input on the right (e.g. a show-password button). */
  trailing?: ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, hint, error, optional, trailing, className, ...rest },
  ref,
) {
  const id = useId();
  // iPhones show an empty date/time field as a blank box; give it a visible hint.
  const pickerHint =
    (rest.type === 'date' || rest.type === 'time') && !rest.value
      ? (rest.placeholder ?? (rest.type === 'time' ? 'Wybierz godzinę' : 'Wybierz datę'))
      : null;
  return (
    <Field id={id} label={label} hint={hint} error={error} optional={optional}>
      <div className={styles.inputWrap}>
        <input
          ref={ref}
          id={id}
          className={[styles.input, trailing ? styles.withTrailing : '', className ?? ''].join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          {...rest}
        />
        {pickerHint && (
          <span className={styles.iosPlaceholder} aria-hidden="true">
            {pickerHint}
          </span>
        )}
        {trailing && <div className={styles.trailing}>{trailing}</div>}
      </div>
    </Field>
  );
});
